import { Dispatch, FC, ReactElement, SetStateAction, useEffect } from 'react'
import { useMediaQuery } from 'react-responsive'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { isMobile } from '@const/device'
import { BACKEND_URL } from '@const/env'
import { ScormVotes } from '@models/scorm'
import BackButton from '@organisms/BackButton'
import GrayButton from '@organisms/GrayButton'
import { useViewScormMutation } from '@services/scorms'
import GradeBlock from '../GradeBlock'
import NavigationBlock from '../NavigationBlock'
import ExpandButton from './ExpandButton'
import {
  BottomWrapper,
  Frame,
  NavButtonsWrapper,
  Wrapper,
  MobileTopWrapper,
  MobileContentWrapper,
  BottomMobileContentWrapper,
  MobileNavBlockWrapper,
  DesktopExpandInModalWrapper,
  MobileExpandInModalWrapper,
} from './styled'
import RotateMobileBanner from './RotateMobileBanner'

type Props = {
  scormDirectory: string
  inModal: boolean
  toggleInModal: Dispatch<SetStateAction<boolean>>
  scormType: 'articulate' | 'ispring'
  prevRoute?: string | null
  nextRoute?: string | null
  onChangePage: () => Promise<void>
  scormId: string
  scormIri: string | undefined
  currentVote: ScormVotes | null | undefined
  handleBack: () => void
  title: string | undefined
  isClientCourse?: boolean
  themeId: string
  isTestBlocked?: boolean
  onBlockedNavigation?: (pendingNavigation?: () => void) => void
  showControlQuestionButton?: boolean
  onControlQuestion?: () => void
  isPreviewMode?: boolean
}

type ContentProps = {
  inModal: boolean
  back: ReactElement
  frame: ReactElement
  expand: ReactElement
  controlQuestionButton: ReactElement | null
  navigation: ReactElement
  gradeBlock: ReactElement | null
}

const MobileContent: FC<ContentProps> = ({
  inModal,
  back,
  frame,
  expand,
  controlQuestionButton,
  navigation,
}) => (
  <MobileContentWrapper inModal={inModal}>
    {!inModal && <MobileTopWrapper>{back}</MobileTopWrapper>}
    {!inModal && <RotateMobileBanner />}
    {frame}
    {inModal ? (
      <MobileExpandInModalWrapper>{expand}</MobileExpandInModalWrapper>
    ) : (
      <BottomMobileContentWrapper>
        <MobileNavBlockWrapper>
          {controlQuestionButton}
          {expand}
          {navigation}
        </MobileNavBlockWrapper>
      </BottomMobileContentWrapper>
    )}
  </MobileContentWrapper>
)

const DesktopContent: FC<ContentProps> = ({
  inModal,
  back,
  frame,
  expand,
  controlQuestionButton,
  navigation,
  gradeBlock,
}) => (
  <>
    {frame}
    {inModal ? (
      <DesktopExpandInModalWrapper>{expand}</DesktopExpandInModalWrapper>
    ) : (
      <BottomWrapper>
        {back}
        {/* Оценка занятия живёт в правой группе, рядом с «Развернуть» (как на проде).
            Отдельным элементом BottomWrapper она из-за space-between уезжала в центр.
            С контрольным вопросом не конфликтует: оценка только у общих курсов,
            кнопка вопроса только у клиентских. */}
        <NavButtonsWrapper>
          {gradeBlock}
          {controlQuestionButton}
          {expand}
          {navigation}
        </NavButtonsWrapper>
      </BottomWrapper>
    )}
  </>
)

const ScormViewer: FC<Props> = ({
  scormDirectory,
  inModal,
  toggleInModal,
  scormType,
  prevRoute,
  nextRoute,
  onChangePage,
  scormId,
  scormIri,
  currentVote,
  handleBack,
  isClientCourse = false,
  themeId,
  isTestBlocked,
  onBlockedNavigation,
  showControlQuestionButton,
  onControlQuestion,
  isPreviewMode,
}) => {
  const landscape = useMediaQuery({ query: '(orientation: landscape)' })
  const [viewScorm] = useViewScormMutation()

  useEffect(() => {
    if (scormIri && !isPreviewMode) {
      viewScorm(scormIri)
    }
  }, [viewScorm, scormIri, isPreviewMode])

  if (!scormDirectory && !isClientCourse) return null

  // Build SCORM URL - use secure route for internal courses, direct path for platform courses
  const scormEntryFile = scormType === 'articulate' ? 'story.html' : 'res/index.html'
  const scormUrl = isClientCourse
    ? `${BACKEND_URL}/secure/course-theme/scorm/${themeId}/${scormEntryFile}`
    : `${BACKEND_URL}/course-themes/${scormDirectory}/${scormEntryFile}`

  const back = (
    <BackButton onClick={handleBack}>
      {isPreviewMode ? 'Назад' : 'Весь курс'}
    </BackButton>
  )
  const frame = (
    <Frame
      frameBorder="0"
      name="contentBlock"
      id="scormFrame"
      src={scormUrl}
      inModal={inModal}
      isLandscape={landscape}
    />
  )
  const expand = (
    <ExpandButton inModal={inModal} toggleInModal={toggleInModal} />
  )
  const controlQuestionButton = showControlQuestionButton ? (
    <GrayButton
      onClick={onControlQuestion}
      leftIcon={isMobile ? <QuestionCircleOutlined /> : undefined}
    >
      {isMobile ? '' : 'Ответить на контрольный вопрос'}
    </GrayButton>
  ) : null
  const gradeBlock = !isClientCourse ? (
    <GradeBlock scormId={scormId} currentVote={currentVote} />
  ) : null
  const navigation = (
    <NavigationBlock
      onChangePage={onChangePage}
      prevRoute={prevRoute}
      nextRoute={nextRoute}
      isTestBlocked={isTestBlocked}
      onBlockedNavigation={onBlockedNavigation}
      isPreviewMode={isPreviewMode}
    />
  )
  return (
    <Wrapper inModal={inModal} isLandscape={landscape}>
      {isMobile ? (
        <MobileContent
          inModal={inModal}
          back={back}
          frame={frame}
          expand={expand}
          controlQuestionButton={controlQuestionButton}
          navigation={navigation}
          gradeBlock={gradeBlock}
        />
      ) : (
        <DesktopContent
          inModal={inModal}
          back={back}
          frame={frame}
          expand={expand}
          controlQuestionButton={controlQuestionButton}
          navigation={navigation}
          gradeBlock={gradeBlock}
        />
      )}
    </Wrapper>
  )
}

export default ScormViewer
